// Tachograph files: the archive of .ddd downloads (driver cards and vehicle units) with an upload
// box for files from this computer. Remote downloads will land in the same list later.
import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Customer, customers as customersApi, tacho, TachoFile } from '../api';
import { DataGrid } from '../components/DataGrid';
import { Link } from '../components/Links';
import { SearchSelect } from '../components/SearchSelect';
import { dayOnly } from '../format';
import { TachoTabs } from './TachoDrivers';

const MAX_MB = 10;
export const GENERATION_LABEL = { gen1: 'Gen 1', gen2v1: 'Smart (Gen 2)', gen2v2: 'Smart v2' } as const;
export const KIND_LABEL = { card: 'Driver card', vu: 'Vehicle unit' } as const;
const tachoDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
export const periodText = (from: string | null, to: string | null) => (from && to ? (from === to ? tachoDay(from) : `${tachoDay(from)} – ${tachoDay(to)}`) : '–');
/** Who or what a file is about: the driver for a card, the vehicle for a VU. */
export const subjectText = (f: Pick<TachoFile, 'kind' | 'driverName' | 'registration' | 'vehicleName'>) =>
  (f.kind === 'card' ? f.driverName || 'Unknown driver' : f.registration || f.vehicleName || 'Unknown vehicle');

type Result = { name: string; file?: TachoFile; duplicate?: boolean; error?: string };

export function TachoFiles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const kind = (params.get('kind') ?? '') as '' | 'card' | 'vu';
  const owner = params.get('customer') ?? '';
  const [typed, setTyped] = useState(q);
  const [rows, setRows] = useState<TachoFile[] | null>(null);
  const [custs, setCusts] = useState<Customer[]>([]);
  const [error, setError] = useState('');
  const [cardCustomer, setCardCustomer] = useState('');
  const [busy, setBusy] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      if (typed.trim() !== q) setParams((p) => { typed.trim() ? p.set('q', typed.trim()) : p.delete('q'); return p; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [typed, q, setParams]);
  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  useEffect(() => {
    tacho.list({ q, kind, customerId: owner === 'none' ? 'none' : owner ? Number(owner) : null })
      .then((r) => { setRows(r.files); setError(''); }).catch((e) => setError(e.message));
  }, [q, kind, owner, reload]);

  const customerOptions = useMemo(() => custs.map((c) => ({ value: String(c.id), label: c.name })), [custs]);

  const upload = async (files: File[]) => {
    if (!files.length) return;
    setResults([]);
    const done: Result[] = [];
    for (const [i, file] of files.entries()) {
      if (file.size > MAX_MB * 1024 * 1024) { done.push({ name: file.name, error: `larger than ${MAX_MB} MB, so not a tachograph file.` }); continue; }
      setBusy(files.length > 1 ? `Opening ${i + 1} of ${files.length}…` : 'Opening…');
      try {
        const r = await tacho.upload(file, cardCustomer ? Number(cardCustomer) : null);
        done.push({ name: file.name, file: r.file, duplicate: r.duplicate });
      } catch (err) {
        done.push({ name: file.name, error: (err as Error).message });
      }
    }
    setBusy('');
    // One file that opened: show it straight away. Several: list what happened to each.
    if (done.length === 1 && done[0].file) return navigate(`/tacho/${done[0].file.id}`, { state: { duplicate: done[0].duplicate } });
    setResults(done);
    setReload((n) => n + 1);
  };
  const picked = (e: ChangeEvent<HTMLInputElement>) => { const f = [...(e.target.files ?? [])]; e.target.value = ''; void upload(f); };
  const dropped = (e: DragEvent) => { e.preventDefault(); setDragging(false); void upload([...e.dataTransfer.files]); };

  return (
    <div className="page wide">
      <div className="page-head"><h1>Tachograph</h1></div>
      <TachoTabs />

      <section className={`card tacho-drop${dragging ? ' dragging' : ''}`} aria-label="Open a file from this computer"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={dropped}>
        <div>
          <h2>Open a file from this computer</h2>
          <p className="muted small">Driver card or vehicle unit downloads (.ddd, .tgd, .v1b, .c1b), all generations. Drop them here or choose them.
            Each file is kept in the archive below.</p>
        </div>
        <div className="tacho-drop-actions">
          <label htmlFor="tacho-card-customer" className="small">Customer for driver card files <span className="muted">(optional)</span></label>
          <SearchSelect id="tacho-card-customer" value={cardCustomer} options={customerOptions} emptyLabel="– not yet –" onChange={setCardCustomer} />
          <p className="hint">Vehicle unit files are filed to the vehicle (and its customer) by VIN or registration.</p>
          <button type="button" className="primary" disabled={!!busy} onClick={() => input.current?.click()}>{busy || 'Choose files…'}</button>
          <input ref={input} type="file" multiple hidden accept=".ddd,.DDD,.tgd,.TGD,.v1b,.V1B,.c1b,.C1B,.esm,application/octet-stream" onChange={picked} />
        </div>
        {results.length > 0 && (
          <ul className="tacho-results" aria-live="polite">
            {results.map((r, i) => (
              <li key={i} className={r.error ? 'error' : undefined}>
                {r.file
                  ? <><Link to={`/tacho/${r.file.id}`}>{subjectText(r.file)}</Link> <span className="muted">· {r.name}{r.duplicate ? ' · was already in the archive' : ''}</span></>
                  : <><b>{r.name}</b>: {r.error}</>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {error && <p className="error" role="alert">{error}</p>}
      <div className="toolbar">
        <label className="sr-only" htmlFor="tacho-search">Search</label>
        <input id="tacho-search" type="search" placeholder="Search driver, card, registration, VIN or customer…" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <label className="sr-only" htmlFor="tacho-kind">Kind</label>
        <select id="tacho-kind" value={kind} onChange={(e) => setParams((p) => { e.target.value ? p.set('kind', e.target.value) : p.delete('kind'); return p; }, { replace: true })}>
          <option value="">Cards and vehicle units</option>
          <option value="card">Driver cards</option>
          <option value="vu">Vehicle units</option>
        </select>
        <label className="sr-only" htmlFor="tacho-customer">Customer</label>
        <SearchSelect id="tacho-customer" value={owner} options={[{ value: 'none', label: 'No customer yet' }, ...customerOptions]} emptyLabel="All customers"
          onChange={(v) => setParams((p) => { v ? p.set('customer', v) : p.delete('customer'); return p; }, { replace: true })} />
      </div>
      {rows && (
        <DataGrid id="tacho-files" rows={rows} rowKey={(f) => f.id} noun={['file', 'files']} href={(f) => `/tacho/${f.id}`}
          defaultSort={{ key: 'downloaded', dir: -1 }}
          empty={q || kind || owner ? 'No files match.' : 'No tachograph files yet. Open one from this computer above.'} columns={[
            { key: 'subject', label: 'Driver / vehicle', always: true, value: subjectText,
              cell: (f) => <Link to={`/tacho/${f.id}`}><strong>{subjectText(f)}</strong></Link> },
            { key: 'kind', label: 'Kind', value: (f) => KIND_LABEL[f.kind] },
            { key: 'number', label: 'Card / VIN', value: (f) => (f.kind === 'card' ? f.cardNumber : f.vin) ?? '', nowrap: true },
            { key: 'customer', label: 'Customer', value: (f) => f.customerName ?? '',
              cell: (f) => f.customerName ?? <span className="muted">–</span> },
            { key: 'period', label: 'Activities', value: (f) => f.periodTo ?? '', csv: (f) => periodText(f.periodFrom, f.periodTo),
              cell: (f) => periodText(f.periodFrom, f.periodTo), nowrap: true },
            { key: 'downloaded', label: 'Downloaded', value: (f) => f.downloadedAt ?? f.uploadedAt ?? '',
              sortBy: (f) => Date.parse(f.downloadedAt ?? f.uploadedAt ?? '') || 0,
              csv: (f) => (f.downloadedAt ? dayOnly(f.downloadedAt) : ''), cell: (f) => (f.downloadedAt ? dayOnly(f.downloadedAt) : <span className="muted">–</span>), nowrap: true },
            { key: 'generation', label: 'Tachograph', value: (f) => GENERATION_LABEL[f.generation], hidden: true },
            { key: 'source', label: 'Source', value: (f) => (f.source === 'remote' ? 'Remote download' : `Uploaded${f.uploadedBy ? ` by ${f.uploadedBy}` : ''}`), hidden: true },
            { key: 'file', label: 'File name', value: (f) => f.fileName, hidden: true },
          ]} />
      )}
    </div>
  );
}
